import PropTypes from 'prop-types';
import React from 'react';

import './MapLegend.css';

const SWATCH_SIZE = 12;

/**
 * A color square. Colors come from the map document (data), so they are SVG
 * `fill` attributes rather than inline CSS (hub CLAUDE.md "No Inline CSS"),
 * and mapDocument.js has already rejected anything that isn't a hex color.
 * Decorative: the entry's text label next to it carries the meaning.
 */
function Swatch({ color }) {
    return (
        <svg
            className="map-legend__swatch-svg"
            width={SWATCH_SIZE}
            height={SWATCH_SIZE}
            aria-hidden="true"
            focusable="false"
        >
            <rect width={SWATCH_SIZE} height={SWATCH_SIZE} fill={color} />
        </svg>
    );
}

Swatch.propTypes = { color: PropTypes.string.isRequired };

/**
 * A proportional legend entry: a circle at the radius the engine resolved for
 * it, the same size the matching markers draw at. Every circle in one legend
 * shares a box as wide as the largest one (`boxRadius`), so the labels next to
 * them line up.
 */
function SizeCircle({ color, radius, boxRadius }) {
    const size = boxRadius * 2 + 2;
    return (
        <svg
            className="map-legend__circle-svg"
            width={size}
            height={size}
            aria-hidden="true"
            focusable="false"
        >
            <circle cx={size / 2} cy={size / 2} r={radius} fill={color} />
        </svg>
    );
}

SizeCircle.propTypes = {
    color: PropTypes.string.isRequired,
    radius: PropTypes.number.isRequired,
    boxRadius: PropTypes.number.isRequired,
};

/**
 * One layer's legend, drawn exactly as the map document sends it: the layer
 * label, `legend.title`, then `legend.entries` in order. Nothing is derived:
 * no domain formatting, no palette, no added entries.
 *
 * Each entry is drawn by what it carries:
 * - `radius` and `color`: a circle of that size (proportional layers)
 * - `color`: a square swatch (sequential bands, categorical classes, no data)
 * - neither: its text alone (a single style's "why this layer is one color")
 */
export default function MapLegend({ layerLabel, legend }) {
    const boxRadius = Math.max(
        0,
        ...legend.entries.map((entry) => entry.radius || 0)
    );

    return (
        <div className="map-legend small">
            <p className="mb-1">
                <span className="fw-semibold">{String(layerLabel)}</span>
                {legend.title && (
                    <span className="text-muted">
                        {' '}
                        &middot; {String(legend.title)}
                    </span>
                )}
            </p>
            {legend.entries.length > 0 && (
                <ul className="list-unstyled mb-0">
                    {legend.entries.map((entry, i) => (
                        <li
                            key={`${entry.label}-${i}`}
                            className="d-flex align-items-center gap-2"
                        >
                            {entry.color && entry.radius ? (
                                <SizeCircle
                                    color={entry.color}
                                    radius={entry.radius}
                                    boxRadius={boxRadius}
                                />
                            ) : (
                                entry.color && <Swatch color={entry.color} />
                            )}
                            <span>{String(entry.label)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

MapLegend.propTypes = {
    /** The layer's label, so multi-layer legends say which layer they key */
    layerLabel: PropTypes.string.isRequired,
    /** The layer's legend from the map document, already validated */
    legend: PropTypes.shape({
        title: PropTypes.string,
        kind: PropTypes.string,
        entries: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                color: PropTypes.string,
                radius: PropTypes.number,
            })
        ).isRequired,
    }).isRequired,
};
